/**
 * Маркетинговый трекинг, который раньше жил строкой в админке CarrotQuest
 * (поле «Tracking Master») и исполнялся вендором через `eval`. Под CSP без
 * `'unsafe-eval'` он молча перестал работать, поэтому переехал в бандл — здесь
 * он покрывается `script-src 'self'`, типами и тестами (AI-102).
 *
 * Селекторы при переносе исправлены: в админке они разъехались с вёрсткой и
 * часть событий не отправлялась задолго до CSP.
 */

const CLICK_EVENTS = [
  {
    selector:
      '[data-testid="landing_page_hero_upload_button"],[data-testid="landing_page_final_cta_upload_button"]',
    eventName: 'Кликнул - загрузить презентацию',
  },
  {
    selector:
      '[data-testid="landing_page_hero_example_button"],[data-testid="landing_page_final_cta_example_button"]',
    eventName: 'Кликнул - примеры презентаций',
  },
  {
    // На узких экранах кнопка в шапке скрыта, работает пункт мобильного меню —
    // в админской копии его не было, и мобильные клики в воронку не попадали.
    selector:
      '[data-testid="landing_header_apply_button"],[data-testid="landing_header_mobile_menu_apply"]',
    eventName: 'Кликнул - хочу инвестировать (аи)',
  },
];

/**
 * Кнопка отправки формы анализа. Оба варианта настоящие: на странице форма
 * получает префикс родителя (`CreateAnalysisPage.tsx:42` → `${dataTest}_form`),
 * а собственный дефолт компонента (`create_analysis_form`) работает, когда
 * форму рендерят отдельно. Ловим оба, чтобы событие не пропало от смены
 * префикса — ровно так эта связка уже ломалась в копии из админки.
 */
const ANALYSIS_SUBMIT_SELECTOR =
  '[data-testid="create_analysis_page_form_submit_button"],[data-testid="create_analysis_form_submit_button"]';
const SIGN_UP_FORM_SELECTOR = '[data-testid="signup_form"]';

/**
UTM-метки безопасны для передачи вовне, остальная query-строка — нет.
*/
const ALLOWED_QUERY_PARAMETERS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]);

const HALF_PAGE_EVENT = 'Прокрутил страницу до середины';
const FULL_PAGE_EVENT = 'Прокрутил страницу до конца';

/**
 * Свойство «Страница обращения» уезжает во внешний сервис и остаётся в профиле
 * навсегда, поэтому отдаём только маршрут и рекламные метки. Полный
 * `location.href` нельзя: в query-строке маршрутов подтверждения регистрации
 * ходят почта, телефон и токены.
 */
const buildSafePageUrl = () => {
  const url = new URL(globalThis.location.href);
  const safeUrl = new URL(url.origin + url.pathname);

  for (const [name, value] of url.searchParams) {
    if (ALLOWED_QUERY_PARAMETERS.has(name)) {
      safeUrl.searchParams.set(name, value);
    }
  }

  return safeUrl.href;
};

const handleClick = (event: MouseEvent) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  for (const { selector, eventName } of CLICK_EVENTS) {
    if (event.target.closest(selector)) {
      globalThis.carrotquest?.track(eventName);
      return;
    }
  }
};

const isInsideAnalysisForm = (element: Element) =>
  Boolean(element.closest('form')?.querySelector(ANALYSIS_SUBMIT_SELECTOR));

const trackUpload = (fileName: string) => {
  globalThis.carrotquest?.track('Загрузил презентацию', {
    'Имя файла': fileName,
  });
};

/**
Выбор файла через диалог.
*/
const handleFileChange = (event: Event) => {
  const input = event.target;
  if (
    !(input instanceof HTMLInputElement) ||
    input.type !== 'file' ||
    !input.files?.length ||
    !isInsideAnalysisForm(input)
  ) {
    return;
  }

  trackUpload(input.files[0].name);
};

/**
 * Перетаскивание файла в дропзону. Отдельный обработчик нужен потому, что
 * react-dropzone в этом случае забирает файл из `dataTransfer` и `change`
 * на инпуте не происходит — в админской копии этот путь загрузки терялся.
 *
 * Оговорка: событие отправляется до валидации дропзоны, поэтому отклонённый
 * файл (неподдерживаемый тип, превышение размера) тоже попадёт в воронку —
 * так же, как это работало из админки.
 */
const handleFileDrop = (event: DragEvent) => {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }

  const fileName = event.dataTransfer?.files?.[0]?.name;
  if (!fileName || !isInsideAnalysisForm(target)) {
    return;
  }

  trackUpload(fileName);
};

const handleSubmit = (event: Event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (form.querySelector(ANALYSIS_SUBMIT_SELECTOR)) {
    globalThis.carrotquest?.track('Начал анализ презентации');
    return;
  }

  // Из админской копии сюда же приезжал `identify` с именем, почтой и
  // телефоном прямо из полей формы. Не переносим: обработчик срабатывает до
  // валидации, до отметки о согласии и до ответа сервера, то есть контакты
  // ушли бы вендору даже с неудачной попытки регистрации. Профиль обогащает
  // `carrotQuestIdentifyUser` — из ответа API и только после успешного
  // `carrotquest.auth` (AI-104).
  if (form.matches(SIGN_UP_FORM_SELECTOR)) {
    globalThis.carrotquest?.track('Зарегистрировался - AI');
  }
};

/**
 * Глубина прокрутки считается на каждое событие, а не один раз при подписке:
 * лендинг догружает секции, и высота страницы меняется уже после монтирования.
 *
 * Замер идёт в кадре анимации, а не прямо в обработчике: `scrollHeight`
 * форсирует layout, а событие прилетает на каждый кадр прокрутки. После того
 * как обе цели отправлены, слушатель снимается — считать больше нечего, а
 * висит он на всех страницах приложения, не только на лендинге.
 */
const createScrollTracker = () => {
  let isHalfTracked = false;
  let isFullTracked = false;
  let isMeasureScheduled = false;
  let pendingFrame: number | undefined;

  const stop = () => {
    globalThis.removeEventListener('scroll', handleScroll);
    // Уже запланированный кадр надо отменить: иначе после размонтирования
    // менеджера он отправит цель, а при ремоунте — задвоит её с новой подпиской.
    if (pendingFrame !== undefined) {
      globalThis.cancelAnimationFrame(pendingFrame);
      pendingFrame = undefined;
    }
  };

  const measureScrollDepth = () => {
    isMeasureScheduled = false;
    pendingFrame = undefined;

    const scrollLimit =
      document.documentElement.scrollHeight - globalThis.innerHeight;
    if (scrollLimit <= 0) {
      return;
    }

    const scrolled = globalThis.scrollY;

    if (!isHalfTracked && scrolled >= scrollLimit / 2) {
      isHalfTracked = true;
      globalThis.carrotquest?.track(HALF_PAGE_EVENT);
    }

    if (!isFullTracked && scrolled >= scrollLimit) {
      isFullTracked = true;
      globalThis.carrotquest?.track(FULL_PAGE_EVENT);
    }

    if (isHalfTracked && isFullTracked) {
      stop();
    }
  };

  function handleScroll() {
    if (isMeasureScheduled) {
      return;
    }

    isMeasureScheduled = true;
    pendingFrame = globalThis.requestAnimationFrame(measureScrollDepth);
  }

  return { handleScroll, stop };
};

/**
 * Вешает делегированные слушатели на документ и возвращает функцию отписки.
 *
 * @example
 * const stopTracking = setUpTrackingEvents();
 * stopTracking();
 */
export const setUpTrackingEvents = () => {
  const scrollTracker = createScrollTracker();

  document.addEventListener('click', handleClick, { capture: true });
  document.addEventListener('change', handleFileChange, { capture: true });
  document.addEventListener('drop', handleFileDrop, { capture: true });
  document.addEventListener('submit', handleSubmit, { capture: true });
  globalThis.addEventListener('scroll', scrollTracker.handleScroll, {
    passive: true,
  });

  globalThis.carrotquest?.identify([
    {
      op: 'update_or_create',
      key: 'Страница обращения',
      value: buildSafePageUrl(),
    },
  ]);

  return () => {
    document.removeEventListener('click', handleClick, { capture: true });
    document.removeEventListener('change', handleFileChange, { capture: true });
    document.removeEventListener('drop', handleFileDrop, { capture: true });
    document.removeEventListener('submit', handleSubmit, { capture: true });
    scrollTracker.stop();
  };
};
